/**
 * The QUILL engine (vendor `quill`, global `Quill`) - Quill 2 with the snow
 * toolbar restyled onto the theme tokens (scss/editor.scss).
 *
 * Toolbar presets double as Quill format whitelists, so pasting cannot
 * smuggle formats the toolbar does not offer (colors, images, fonts are
 * dropped). The link button routes to the shell's shared g.prompt flow.
 */

const t = (text) => window.g.translate(text);

// preset name -> { toolbar (Quill toolbar spec), formats (whitelist) }
const PRESETS = {
	basic: {
		toolbar: [
			['bold', 'italic', 'underline'],
			[{ list: 'ordered' }, { list: 'bullet' }],
			['link', 'clean'],
		],
		formats: ['bold', 'italic', 'underline', 'list', 'link'],
	},
	full: {
		toolbar: [
			[{ header: [2, 3, 4, false] }],
			['bold', 'italic', 'underline', 'strike'],
			[{ list: 'ordered' }, { list: 'bullet' }],
			[{ align: [] }],
			['blockquote', 'code-block'],
			['link', 'clean'],
		],
		formats: ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'align', 'blockquote', 'code-block', 'link'],
	},
};

// Toolbar controls get translated title/aria-label (the icons carry no
// text). The labels are THUNKS around literal t('…') calls — translation
// stays at render time, but the literals are visible to the static i18n
// scanner (i18n.md "The source scanner"; cursuri #58 — a bare string in
// a table is invisible and escapes the framework translation pack).
const LABELS = [
	['button.ql-bold', () => t('Bold')],
	['button.ql-italic', () => t('Italic')],
	['button.ql-underline', () => t('Underline')],
	['button.ql-strike', () => t('Strikethrough')],
	['button.ql-list[value="ordered"]', () => t('Numbered list')],
	['button.ql-list[value="bullet"]', () => t('Bulleted list')],
	['button.ql-blockquote', () => t('Quote')],
	['button.ql-code-block', () => t('Code block')],
	['button.ql-link', () => t('Link')],
	['button.ql-clean', () => t('Clear formatting')],
	['.ql-header.ql-picker .ql-picker-label', () => t('Paragraph style')],
	['.ql-align.ql-picker .ql-picker-label', () => t('Alignment')],
];

export class QuillEngine {
	constructor(area, opts, shell) {
		this.area = area;
		const preset = PRESETS[opts.toolbar] || PRESETS.basic;
		this.quill = new Quill(area, {
			theme: 'snow',
			placeholder: opts.placeholder,
			formats: preset.formats,
			bounds: area.parentElement,
			modules: {
				toolbar: {
					container: preset.toolbar,
					handlers: { link: () => shell.linkPrompt() },
				},
			},
		});
		if (opts.height) this.quill.root.style.minHeight = opts.height + 'px';
		this.labelToolbar(area.parentElement);

		this.quill.on('text-change', (delta, oldDelta, source) => {
			if (source === 'user') shell.onUserEdit(); // programmatic changes stay silent
		});
		this.quill.on('selection-change', (range, oldRange) => {
			if (range && !oldRange) shell.onFocus();
			if (!range && oldRange) shell.onBlur();
		});
	}

	// Empty = no text AND no embeds (an image-only document is not empty).
	isEmpty() {
		if (this.quill.getText().trim()) return false;
		return !this.quill.getContents().ops.some((op) => typeof op.insert === 'object');
	}

	html() {
		// Quill 2.0.3's semantic serializer emits EVERY space as &nbsp;
		// (quilljs/quill#4509) - stored content would never word-wrap.
		// Restore normal spaces (consecutive ones may collapse; fine).
		return this.quill.getSemanticHTML().replace(/&nbsp;/g, ' ');
	}

	text() {
		return this.quill.getText().replace(/\n$/, '');
	}

	setHTML(html) {
		this.quill.setContents(this.quill.clipboard.convert({ html }), 'silent');
	}

	enable(on) {
		this.quill.enable(on);
	}

	focus() {
		this.quill.focus();
	}

	linkState() {
		const range = this.quill.getSelection(true);
		return { href: this.quill.getFormat(range).link || '', range };
	}

	// Empty url removes the link; a collapsed selection inserts the url as
	// its own linked text.
	applyLink(url, state) {
		this.quill.setSelection(state.range);
		if (!url) {
			this.quill.format('link', false, 'user');
		} else if (state.range.length) {
			this.quill.format('link', url, 'user');
		} else {
			this.quill.insertText(state.range.index, url, { link: url }, 'user');
		}
	}

	labelToolbar(scope) {
		const bar = scope.querySelector('.ql-toolbar');
		if (!bar) return;
		for (const [selector, label] of LABELS) {
			for (const control of bar.querySelectorAll(selector)) {
				control.setAttribute('title', label());
				control.setAttribute('aria-label', label());
			}
		}
	}

	destroy() {
		// nothing quill-specific to release; the shell removes the DOM
	}
}
